import { formatDate } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { __, getEnglishOrdinalSuffix, ModalComponent } from '@web/birdz-angular';
import { Kpi } from 'app/interfaces/kpi.interface';
import { EXTRA_KPIS, KpiOverviewItem } from 'app/interfaces/kpi-overview-item.interface';
import { add, endOfISOWeek } from 'date-fns';

@Component({
  selector: 'app-modal-help-contract-config',
  templateUrl: './modal-help-contract-config.component.html',
  styleUrls: ['./modal-help-contract-config.component.scss'],
  imports: [TranslocoPipe, ModalComponent],
})
export class ModalHelpContractConfigComponent {
  data = inject<{
    kpis: (Kpi & { description: string /* sewerin (only used here) */ })[];
    techno: string | null;
    overview?: KpiOverviewItem[];
  }>(MAT_DIALOG_DATA);
  transloco = inject(TranslocoService);
  // raw kpis are explained once by the "raw" definition, no need to list each of them
  kpis = this.data.kpis.filter((kpi) => !kpi.raw_display);
  // non-compliance counts displayed below their kpi in the summary card, explained here
  extraKpis = this.kpis.flatMap((kpi) => {
    const extra = this.data.overview?.find((item) => item.id === kpi.id)?.extra;
    return (EXTRA_KPIS[this.data.techno ?? ''] ?? [])
      .filter((extraKpi) => extra?.[extraKpi])
      .map((extraKpi) => ({ kpi, extraKpi }));
  });
  showExplanation = false;

  get hasRawKpis(): boolean {
    return this.data.kpis.some((kpi) => kpi.has_raw_equivalent);
  }

  getKpiLabel = (kpi: Kpi) =>
    kpi.label + (kpi.daily_display && !kpi.ytd_display ? ` ${__('DAILY').toLowerCase()}` : '');

  getContractualKpiLabel = (kpi: Kpi) => kpi.label.replace(/\s*(YTD|AAJ)/g, '');

  getKpiDescription = (kpi: Kpi) => {
    if (this.data.techno === 'kapta') {
      return __(`${kpi.id}_DESC`);
    }

    const activeTimeframe = ['yearly', 'ytd', 'daily'].find((tf) => kpi[`${tf}_display` as keyof Kpi]);
    return activeTimeframe
      ? __(`CONTRACT_CONFIG_HELP.SEWERIN.${activeTimeframe.toUpperCase()}_${kpi.type}`, {
          kpi: this.getContractualKpiLabel(kpi),
        })
      : '';
  };

  constructor() {
    this.showExplanation = this.kpis.some((kpi) => kpi.weekly_display || kpi.monthly_display);
  }

  generateExplanation(kpi: Kpi): string {
    const rawExplanation = [this.getWeeklyExplanation(kpi), this.getMonthlyExplanation(kpi)].join(' ');
    const formattedHtmlExplanation = this.formatHtmlExplanation(rawExplanation);

    return formattedHtmlExplanation;
  }

  private getWeeklyExplanation(kpi: Kpi): string {
    if (kpi.weekly_display && kpi.weekly_day) {
      return __('CONTRACT_CONFIG_HELP.KPI_CALCULATIONS.WEEKLY', {
        kpi: kpi.label,
        day: this.formatWeeklyDay(kpi.weekly_day),
        depth: kpi.weekly_depth,
      });
    }

    return '';
  }

  private getMonthlyExplanation(kpi: Kpi): string {
    if (kpi.monthly_display && kpi.monthly_day) {
      return __('CONTRACT_CONFIG_HELP.KPI_CALCULATIONS.MONTHLY', {
        kpi: kpi.label,
        day: this.formatMonthlyDay(kpi.monthly_day.toString()),
        depth: kpi.monthly_depth,
      });
    }

    return '';
  }

  private formatWeeklyDay(day: number): string {
    const currentLang = this.transloco.getActiveLang();
    const date = add(endOfISOWeek(new Date()), { days: day });
    return formatDate(date, 'EEEE', currentLang);
  }

  private formatMonthlyDay(day: string): string {
    const currentLang = this.transloco.getActiveLang();

    if (currentLang === 'en') {
      const dayNum = parseInt(day);
      const suffix = getEnglishOrdinalSuffix(dayNum);
      return `${day}${suffix}`;
    }

    return day;
  }

  private formatHtmlExplanation(explanation: string) {
    // apply bold class on **[...]** substrings
    const boldRegex = /\*\*(.*?)\*\*/g;
    explanation = explanation.replaceAll(boldRegex, '<span class="kpi-label">$1</span>');

    // apply semibold class on *[...]* substrings
    const semiboldRegex = /\*(.*?)\*/g;
    explanation = explanation.replaceAll(semiboldRegex, '<span class="kpi-calculation-number">$1</span>');

    return explanation;
  }
}
